<i18n>
  {
    "fr": {
      "placeholder": "mer. 1/01/30",
      "passport_number": "Numéro de passeport",
      "expiration-date": "Date d'expiration",
      "emission-country": "Pays d'émission",
      "errors": {
        "required": "Champ requis"
      }
    },
    "en": {
      "placeholder": "Wen. 1/01/30",
      "passport_number": "Passport number",
      "expiration-date": "Expiration date",
      "emission-country": "Emission country",
      "errors": {
        "required": "Required field"
      }
    }
  }
</i18n>

<template>
  <v-row
    no-gutters
  >
    <v-col class="pr-2">
      <v-text-field
        v-model="passport.number"
        label="passport-number"
        placeholder="12RF12345"
        height="42px"
        dense
        outlined
      >
        <template
          #label
        >
          {{ $t('passport_number' ) }}
          <span
            v-if="!passport.number"
            class="red--text"
          >
            <strong> *</strong>
          </span>
        </template>
      </v-text-field>
    </v-col>

    <v-col class="px-2">
      <v-menu
        ref="menu"
        v-model="passportExpirationMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        max-width="290"
      >
        <template #activator="{on}">
          <v-text-field
            v-model="formatExpirationDate"
            :placeholder="$t('placeholder')"
            :label="$t('expiration-date')"
            readonly
            height="42px"
            dense
            outlined
            v-on="on"
          >
            <template
              #label
            >
              {{ $t('expiration-date' ) }}
              <span
                v-if="!formatExpirationDate"
                class="red--text"
              >
                <strong> *</strong>
              </span>
            </template>
          </v-text-field>
        </template>
        <v-date-picker
          ref="picker"
          v-model="passport.expirationDate"
          :min="minExpirationDate"
          :max="maxExpirationDate"
          :locale="localLang"
          first-day-of-week="1"
          scrollable
          no-title
          @input="passportExpirationMenu = false"
          @change="save"
        />
      </v-menu>
    </v-col>
    <v-col class="pl-2">
      <v-select
        v-model="passport.emissionCountry"
        :items="countries"
        item-text="native_name"
        item-value="alpha_2_code"
        :menu-props="{'offset-y': true, 'auto': true}"
        label="emission-country"
        height="42px"
        dense
        outlined
      >
        <template #item="{ item }">
          <div class="v-list-item__title">
            {{ item.alpha_2_code }} - {{ item.native_name }}
          </div>
        </template>
        <template
          #label
        >
          {{ $t('emission-country' ) }}
          <span
            v-if="!passport.emissionCountry"
            class="red--text"
          >
            <strong> *</strong>
          </span>
        </template>
      </v-select>
    </v-col>
  </v-row>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import _ from 'lodash'

export default {
  name: 'FlightPassportDetails',
  mixins: [validationMixin],
  props: {
    traveller: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    passport: {
      registration_number: '',
      number: '',
      expirationDate: '',
      emissionCountry: ''
    },
    passportExpirationMenu: false
  }),
  computed: {
    ...mapGetters({
      countries: 'general/getCountries'
    }),
    /**
     * Format the local language to a different ISO, because vuetify date picker need this format
     * @return {string}
     */
    localLang: function () {
      let lang
      if (this.$i18n.locale === 'fr') {
        lang = 'fr-FR'
      } else if (this.$i18n.locale === 'en') {
        lang = 'en-GB'
      }
      return lang
    },
    /**
     * Format the expiration date based on the i18n locale
     * @return {string}
     */
    formatExpirationDate () {
      const date = this.passport.expirationDate
      let formatDate
      if (date === '') {
        formatDate = null
      } else {
        const momentDate = moment(date)
        if (this.$i18n.locale === 'fr') {
          momentDate.locale('fr')
          formatDate = momentDate.format('ddd D/MM/YY')
        } else if (this.$i18n.locale === 'en') {
          momentDate.locale('en')
          formatDate = momentDate.format('ddd DD/MM/YY')
        } else {
          formatDate = null
        }
      }
      return formatDate
    },
    /**
     * Return the current date to a YYYY-MM-DD format for the Vuetify datepicker to use
     * @return {string}
     */
    minExpirationDate () {
      const now = moment()
      return now.format('YYYY-MM-DD')
    },
    /**
     * Return the date 20 years from current date to a YYYY-MM-DD format for the Vuetify datepicker to use
     * @return {string}
     */
    maxExpirationDate () {
      const now = moment()
      now.add(20, 'years')
      return now.format('YYYY-MM-DD')
    },
    /**
     * Return an array of string with errors or nothing based on vuelidate state
     * @return {string[]}
     */
    passportNumberErrors () {
      const errors = []
      if (!this.$v.passport.number.$dirty) return errors
      !this.$v.passport.number.required && errors.push(this.$i18n.t('errors.required'))
      return errors
    },
    /**
     * Return an array of string with errors or nothing based on vuelidate state
     * @return {string[]}
     */
    passportExpirationDateErrors () {
      const errors = []
      if (!this.$v.passport.expirationDate.$dirty) return errors
      !this.$v.passport.expirationDate.required && errors.push(this.$i18n.t('errors.required'))
      return errors
    },
    /**
     * Return an array of string with errors or nothing based on vuelidate state
     * @return {string[]}
     */
    passportEmissionCountryErrors () {
      const errors = []
      if (!this.$v.passport.emissionCountry.$dirty) return errors
      !this.$v.passport.emissionCountry.required && errors.push(this.$i18n.t('errors.required'))
      return errors
    },
    /**
     * Return the opposite of vuelidate invalid parameters (to improve readability of code)
     * This value need to be computed for the watcher to work properly
     * @return {boolean}
     */
    validity () {
      return !this.$v.$invalid
    }
  },
  watch: {
    passport: {
      /**
       * Call to updatePassportDetails method while deep cloning the newVal param to avoid pointer error
       * @param newVal {Object}
       * @return {undefined}
       */
      handler: function (newVal) {
        newVal.registration_number = this.traveller.registration_number
        this.updatePassportDetails(_.cloneDeep(newVal))
      },
      deep: true
    },
    /**
     * Modify picker activepicker, this allow the "birthday" style datepicker to work properly.
     * (Year selection first, then month and in the end the day)
     * @param val {string}
     */
    passportExpirationMenu (val) {
      val && setTimeout(() => (this.$refs.picker.activePicker = 'YEAR'))
    },
    /**
     * Watch for value change of validity then commit the new value to the store
     * @param newVal {boolean}
     */
    validity (newVal) {
      this.$emit('validity', newVal)
    }
  },
  validations: {
    passport: {
      number: { required },
      expirationDate: { required },
      emissionCountry: { required }
    }
  },
  methods: {
    ...mapActions({
      updatePassportDetails: 'fb/passengersInformation/changeSelectedTravellersPassportDetails'
    }),
    /**
     * This method is used by the Vuetify datepicker and allow the birthday style datepicker
     * @param date {string}
     */
    save (date) {
      this.$refs.menu.save(date)
    }
  }
}
</script>

<style scoped>

</style>
